/**
 * Deposits API Client — per-gép kaució refund/retain (Slice A)
 *
 * BE-contract (deposits.controller, JWT-auth, FeatureFlag.BERLES):
 *   POST /deposits/refund  body { depositId, amount, reason? } — teljes visszatérítés
 *   POST /deposits/retain  body { depositId, reason }          — reason KÖTELEZŐ
 *
 * A BE plain NestJS-exception-öket dob (NotFound/Conflict/UnprocessableEntity)
 * magyar message-ekkel, strukturált `error.code` NEM garantált → a toast-helper
 * defenzív: előbb `error.code` (ha van), különben HTTP-status-fallback.
 *
 * Slice B (#688 follow-up): partial-refund (parciális kár-levonás).
 *   POST /deposits/partial-refund body { depositId, retainedAmount, damageDescription, vatRate? }
 *   — `retainedAmount` = a VISSZATARTANDÓ / kár összeg (NEM a visszajáró).
 *
 * Slice C (#689 follow-up): transfer-to-worksheet (kaució → szerviz-munkalap-előleg átvezetés).
 *   POST /deposits/transfer-to-worksheet body { depositId, worksheetId, amount? }
 *   — `amount` undefined = a teljes nyitott kaució átvezetése. A `transferredAmount`
 *     money-truth a BE-válaszból jön (NEM a FE számolja).
 */

import { ApiError, api } from './client';

/** POST /deposits/refund body (a `depositId`-t a hívó adja külön). */
export interface RefundDepositDto {
  /** A teljes visszatérítendő összeg = a deposit teljes `amount`-ja (DRY-money: a FE NEM számol). */
  amount: number;
  /** Opcionális indok. */
  reason?: string | undefined;
}

/** POST /deposits/retain body (a `depositId`-t a hívó adja külön). */
export interface RetainDepositDto {
  /** Visszatartás indoka — KÖTELEZŐ (BE @MaxLength(450)). */
  reason: string;
}

/**
 * POST /deposits/partial-refund body (a `depositId`-t a hívó adja külön).
 *
 * BE-contract (deposit-sale.service.ts `PartialRefundDepositInput`):
 *   - `retainedAmount` = a VISSZATARTANDÓ / kár összeg (a maradék automatikusan visszajár).
 *   - `damageDescription` = kár szöveges leírása (KÖTELEZŐ, BE @MaxLength(400)).
 *   - `vatRate?` = ÁFA-kulcs string (BE `VatRate`: RATE_27 | RATE_18 | RATE_5 | RATE_0; default RATE_27).
 */
export interface PartialRefundDepositDto {
  /** A visszatartandó (kár) összeg HUF egészben. A `deposit.amount − retainedAmount` automatikusan visszajár. */
  retainedAmount: number;
  /** Kár szöveges leírása (KÖTELEZŐ, max 400 karakter). */
  damageDescription: string;
  /** ÁFA-kulcs string (RATE_27 | RATE_18 | RATE_5 | RATE_0); ha nincs → BE default RATE_27. */
  vatRate?: string | undefined;
}

/** POST /deposits/partial-refund válasz `data` (deposit-sale.service.ts `PartialRefundDepositResult`). */
export interface PartialRefundDepositResult {
  depositId: string;
  /** Wire-enum: 'partially_retained' (van visszajáró) vagy 'retained' (kár ≥ kaució). */
  status: string;
  /** A ténylegesen visszajáró összeg (money-truth, BE-számolt). */
  returnedAmount: number;
  /** A ténylegesen visszatartott (kár) összeg (BE clamp után). */
  retainedAmount: number;
  /** Kár-számla ID, ha az InvoiceService inject-elve van → link a számla-oldalra. */
  damageInvoiceId?: string | undefined;
  /** Forfeit-journal kár-nettó (csak ÚJ levonásnál). */
  damageNet?: number | undefined;
  /** Forfeit-journal ÁFA (csak ÚJ levonásnál). */
  damageVat?: number | undefined;
}

/**
 * Teljes kaució-visszatérítés. `depositId` = a deposit `id` (NEM a rentalItemId).
 */
export async function refundDeposit(
  depositId: string,
  dto: RefundDepositDto
): Promise<{ data: unknown }> {
  return api.post<{ data: unknown }>('/deposits/refund', { depositId, ...dto });
}

/**
 * Parciális kaució-visszatérítés (kár-levonás). `depositId` = a deposit `id`.
 * A FE CSAK a `retainedAmount`-ot (kár) küldi; a visszajáró BE-számolt (returnedAmount).
 */
export async function partialRefundDeposit(
  depositId: string,
  dto: PartialRefundDepositDto
): Promise<{ data: PartialRefundDepositResult }> {
  return api.post<{ data: PartialRefundDepositResult }>('/deposits/partial-refund', {
    depositId,
    ...dto,
  });
}

/**
 * Kaució-visszatartás (HELD/RECEIVED → RETAINED). `depositId` = a deposit `id`.
 */
export async function retainDeposit(
  depositId: string,
  dto: RetainDepositDto
): Promise<{ data: unknown }> {
  return api.post<{ data: unknown }>('/deposits/retain', { depositId, ...dto });
}

/**
 * POST /deposits/transfer-to-worksheet body (a `depositId`-t a hívó adja külön).
 *
 * BE-contract (deposit-sale.service.ts `TransferToWorksheetInput`):
 *   - `worksheetId` = a cél szerviz-munkalap (KÖTELEZŐ; a partner+státusz-guard BE-oldali).
 *   - `amount?` = átvezetendő összeg; ha undefined → a teljes nyitott kaució (BE clamp `deposit.amount`-ra).
 */
export interface TransferToWorksheetDto {
  /** Cél szerviz-munkalap ID. */
  worksheetId: string;
  /** Átvezetendő összeg HUF egészben; ha undefined → a teljes nyitott kaució. */
  amount?: number | undefined;
}

/** POST /deposits/transfer-to-worksheet válasz `data` (deposit-sale.service.ts `TransferToWorksheetResult`). */
export interface TransferToWorksheetResult {
  depositId: string;
  /** Wire-enum: 'partially_retained' (van visszajáró) vagy 'retained' (teljes átvezetés). */
  status: string;
  /** A ténylegesen átvezetett összeg (money-truth, BE-számolt). */
  transferredAmount: number;
  worksheetId: string;
}

/**
 * Kaució → szerviz-munkalap-előleg átvezetés. `depositId` = a deposit `id`.
 * A FE CSAK a `worksheetId`-t (+ opcionális `amount`-ot) küldi; a `transferredAmount`
 * money-truth a BE-válaszból jön. amount nélkül = teljes nyitott kaució.
 */
export async function transferDepositToWorksheet(
  depositId: string,
  dto: TransferToWorksheetDto
): Promise<{ data: TransferToWorksheetResult }> {
  return api.post<{ data: TransferToWorksheetResult }>('/deposits/transfer-to-worksheet', {
    depositId,
    ...dto,
  });
}

/**
 * Hiba → i18n toast-kulcs. Defenzív kétlépcsős feloldás:
 *  1. ha `error instanceof ApiError` és van `error.code` → code-switch (jövő-biztos, ha a BE
 *     strukturált error.code-ot kezd küldeni);
 *  2. különben HTTP-status-fallback (a jelenlegi BE plain-exception-jeire):
 *     404 → notFound · 409 → invalidStatus · 422 → cardNotSupported · 503 → serviceUnavailable.
 *  3. minden más → failed.
 */
export function depositActionErrorToastKey(error: unknown): string {
  if (error instanceof ApiError) {
    // (1) strukturált error.code (ha a BE küldi)
    switch (error.code) {
      case 'DEPOSIT_NOT_FOUND':
        return 'rental.depositActionError.notFound';
      case 'DEPOSIT_INVALID_STATUS':
        return 'rental.depositActionError.invalidStatus';
      case 'DEPOSIT_CARD_NOT_SUPPORTED':
        return 'rental.depositActionError.cardNotSupported';
      case 'DEPOSIT_SERVICE_UNAVAILABLE':
        return 'rental.depositActionError.serviceUnavailable';
      default:
        break;
    }
    // (2) HTTP-status-fallback
    switch (error.statusCode) {
      case 404:
        return 'rental.depositActionError.notFound';
      case 409:
        return 'rental.depositActionError.invalidStatus';
      case 422:
        return 'rental.depositActionError.cardNotSupported';
      case 503:
        return 'rental.depositActionError.serviceUnavailable';
      default:
        return 'rental.depositActionError.failed';
    }
  }
  // (3) nem-ApiError (hálózat, stb.)
  return 'rental.depositActionError.failed';
}
